/**
 * Which of MediaPipe's 478 face landmarks belong to which named feature, and
 * what color to draw each group in — used by `FaceLandmarksExperimentScreen`
 * (and mirrored in `exploring/face-landmarks/index.html`) to make the dots
 * actually useful for eyeballing whether landmarks landed correctly, rather
 * than a uniform speckle that's hard to read against any real photo.
 *
 * The index lists are NOT invented or approximated — they're flattened from
 * MediaPipe's own canonical connector definitions
 * (`face_mesh_connections.py`'s `FACEMESH_LIPS`/`FACEMESH_LEFT_EYE`/etc.,
 * Apache-2.0), fetched and verified before writing this file rather than
 * transcribed from memory, the same discipline
 * `FaceLandmarksExperimentScreen`'s own doc comment already holds itself to
 * for the JS API's response shape. A connector list only encodes edges
 * (pairs of points to draw a line between), so what's stored here is the
 * flattened, deduplicated set of point indices each edge list touches — the
 * region a point belongs to, not the lines between them.
 *
 * Iris indices (468–477) sit outside the 0–467 base mesh entirely — the 478
 * total is 468 base points plus 10 iris points (5 per eye: a center point
 * MediaPipe's connector lists don't include, since a center has no
 * boundary edge to draw, plus 4 points forming the visible iris ring).
 */

export interface FaceRegion {
  label: string;
  color: string;
  indices: ReadonlySet<number>;
}

const LIPS = new Set([
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 185, 40, 39, 37, 0, 267, 269, 270, 409, 78, 95,
  88, 178, 87, 14, 317, 402, 318, 324, 308, 191, 80, 81, 82, 13, 312, 311, 310, 415,
]);

const EYES = new Set([
  // Left eye
  263, 249, 390, 373, 374, 380, 381, 382, 362, 466, 388, 387, 386, 385, 384, 398,
  // Right eye
  33, 7, 163, 144, 145, 153, 154, 155, 133, 246, 161, 160, 159, 158, 157, 173,
]);

const EYEBROWS = new Set([
  // Left eyebrow
  276, 283, 282, 295, 285, 300, 293, 334, 296, 336,
  // Right eyebrow
  46, 53, 52, 65, 55, 70, 63, 105, 66, 107,
]);

const IRISES = new Set([
  // Right iris: center 468 + boundary ring
  468, 469, 470, 471, 472,
  // Left iris: center 473 + boundary ring
  473, 474, 475, 476, 477,
]);

const NOSE = new Set([
  168, 6, 197, 195, 5, 4, 1, 19, 94, 2, 98, 97, 326, 327, 294, 278, 344, 440, 275, 45, 220, 115, 48,
  64,
]);

const FACE_OVAL = new Set([
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
]);

/**
 * Checked in this order — lips/eyes/eyebrows/iris/nose are all more specific
 * than the face outline, so a rare shared boundary point (e.g. where the
 * nose region and the oval both claim a point near the nostril) reads as the
 * more specific feature, not the outline.
 */
export const FACE_REGIONS: readonly FaceRegion[] = [
  { label: 'Lips', color: '#ff1744', indices: LIPS },
  { label: 'Eyes', color: '#ffea00', indices: EYES },
  { label: 'Eyebrows', color: '#ff9100', indices: EYEBROWS },
  { label: 'Iris', color: '#e040fb', indices: IRISES },
  { label: 'Nose', color: '#00e676', indices: NOSE },
  { label: 'Face outline', color: '#00b0ff', indices: FACE_OVAL },
];

/** Everything else — cheeks, forehead, jaw fill — the ~330 of 478 points with no named feature. */
export const OTHER_MESH_COLOR = '#ffffff';
export const OTHER_MESH_LABEL = 'Other mesh points';

export function colorForLandmark(index: number): string {
  for (const region of FACE_REGIONS) {
    if (region.indices.has(index)) return region.color;
  }
  return OTHER_MESH_COLOR;
}
